<template>
  <div class="schema-property">
    <CollapsibleSection
      :default-expanded="defaultExpanded"
      :show-toggle="hasNestedContent"
      :aria-label="parameter ? `Toggle ${parameter}` : undefined"
    >
      <template #trigger>
        <div class="property-label-block" :class="{ 'with-parameter': Boolean(parameter) }">
          <div v-if="parameter" class="parameter base-bold">{{ parameter }}</div>
          <div class="parameter-type-row">
            <div class="parameter-type">
              <span>{{ schema.type }}</span>
              <span v-if="schema.format">({{ schema.format }})</span>
            </div>
            <div class="grow" />
            <div class="parameter-tags pl-2">
              <div v-if="required || schema.required" class="parameter-tag">
                <span class="pill required small">{{ $t('documentation.api.schema.required') }}</span>
              </div>
              <div v-if="customPropertyTag" class="parameter-tag">
                <span class="pill custom-tag small">{{ customPropertyTag }}</span>
              </div>
              <div v-if="customVersionTag" class="parameter-tag">
                <span class="pill version-tag small">{{ customVersionTag }}</span>
              </div>
            </div>
            <div v-if="schema.minimum || schema.minLength" class="parameter-type-info">
              <b>{{ $t('documentation.api.schema.min') }}</b>
              <code v-if="schema.minimum">{{ schema.minimum }}</code>
              <code v-if="schema.minLength">{{ schema.minLength }}</code>
            </div>
            <div v-if="schema.maximum || schema.maxLength" class="parameter-type-info">
              <b>{{ $t('documentation.api.schema.max') }}</b>
              <code v-if="schema.maximum">{{ schema.maximum }}</code>
              <code v-if="schema.maxLength">{{ schema.maxLength }}</code>
            </div>
            <div v-if="schema.default" class="parameter-type-info">
              <b>{{ $t('documentation.api.schema.default') }}</b>
              <code>{{ schema.default }}</code>
            </div>
            <div v-if="schema.example" class="parameter-type-info">
              <b>{{ $t('documentation.api.schema.example') }}</b>
              <code>{{ schema.example }}</code>
            </div>
          </div>
        </div>
      </template>
      <div :class="['parameter-description', { 'with-parameter': Boolean(parameter) }]">
        <div class="divider" />
        <slot name="description" />
        <template v-if="hasNestedContent">
          <api-schema-object
            v-if="schema.items"
            :schema="schema.items"
            :example-generator-args="exampleGeneratorArgs"
          />
          <api-schema-object v-else-if="schema.oneOf || schema.anyOf || schema.properties" :schema="schema" />
        </template>
        <div v-else-if="schema.enum" class="enums text-gray-500">
          <span>{{ $t('documentation.api.schema.mustBeOneOf') }} </span>
          <span v-for="(value, index) in schema.enum" :key="value + index" class="enum">
            <code>{{ value }}</code>
          </span>
        </div>
      </div>
    </CollapsibleSection>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ExampleGeneratorArgs, Schema } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import ApiSchemaObject from '@/components/api/content/operations/schema/ApiSchemaObject.vue';
import CollapsibleSection from '@/components/api/content/common/CollapsibleSection.vue';

const props = withDefaults(
  defineProps<{
    required?: boolean;
    parameter?: string;
    schema: Schema;
    /** When false, the collapsible section starts collapsed. Only applies when the property has nested content. */
    defaultExpanded?: boolean;
    customPropertyTag?: string;
    customVersionTag?: string;
    exampleGeneratorArgs?: ExampleGeneratorArgs;
    type?: 'request' | 'payload' | 'response';
  }>(),
  {
    required: false,
    defaultExpanded: true,
  }
);

const hasNestedContent = computed(() =>
  Boolean(
    props.schema.items ||
      props.schema.oneOf ||
      props.schema.anyOf ||
      (props.schema.properties && Object.keys(props.schema.properties).length > 0)
  )
);
</script>

<style scoped lang="scss">
.property-label-block.with-parameter .parameter-type-row {
  margin-left: 16px;
}

/* Existing parameter / type styles */
.parameter,
.parameter-type {
  font-size: 14px;
  line-height: 20px;
}

.parameter {
  font-weight: 600;
}

.parameter-description {
  font-size: 12px;

  &.with-parameter {
    margin-left: 16px;
  }

  .divider {
    margin: 4px 0;
    height: 1px;
    background-color: #ccc;
  }

  .enums {
    font-weight: 200;
    white-space: pre-wrap;
    word-break: break-all;
  }

  .enum {
    white-space: break-spaces;

    &:not(:last-of-type):after {
      content: ',';
      margin: 0 2px;
    }
  }
}

.parameter-type {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-style: italic;
  font-weight: 200;

  span:not(:last-of-type) {
    margin-right: 4px;
  }
}

.parameter-type-row {
  display: flex;
  flex-direction: row;
  align-items: center;

  .grow {
    flex-grow: 1;
  }

  .pill {
    margin-right: 8px;
  }
  @media screen and (max-width: $mobile-large) {
    flex-wrap: wrap;
  }
}

.parameter-type-info {
  padding: 0 8px;
  border-left: 1px solid #ccc;
  word-break: break-all;

  @media screen and (max-width: $mobile-large) {
    flex-direction: column;
    display: flex;
  }

  b {
    text-transform: uppercase;
    margin-right: 4px;
    font: var(--dev-docs-parameter-type-info-font);
  }
}

.pill.required {
  border: 1px solid #ce2029;
  color: #ce2029;
}
.pill.custom-tag {
  border: 1px solid #666;
  color: #666;
}
.pill.version-tag {
  border: 1px solid #0476d5;
  color: #0476d5;
}
.parameter-tags {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.parameter-tag {
  display: flex;
  justify-content: center;
  align-items: center;
}
.pill.small {
  font: var(--dev-docs-api-spec-required-font-weight);
}
.base-bold {
  font: var(--vvd-typography-base-bold);
}
</style>
